import { useEffect, useState, useMemo, useCallback } from "react";
import { Navigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { LogOut, MessageCircle, Phone, Search, Trash2 } from "lucide-react";
import { useConfig, whatsappHref, telHref } from "@/lib/config";
import ReviewAlertsBanner from "@/components/ReviewAlertsBanner";

const STATUSES = ["new", "contacted", "quoted", "booked", "lost"];
const STATUS_STYLE = {
  new: "bg-blue-100 text-blue-700",
  contacted: "bg-yellow-100 text-yellow-700",
  quoted: "bg-orange-100 text-orange-700",
  booked: "bg-green-100 text-green-700",
  lost: "bg-slate-200 text-slate-600",
};

export default function AdminDashboardPage() {
  const { user, logout } = useAuth();
  const cfg = useConfig();
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState({ total: 0, by_status: {} });
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  const fetchLeads = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter !== "all") params.status = statusFilter;
      if (search) params.search = search;
      const [l, s] = await Promise.all([
        api.get("/leads", { params }),
        api.get("/leads/stats"),
      ]);
      setLeads(l.data);
      setStats(s.data);
    } catch (err) {
      console.error("Failed to load leads:", err);
      toast.error("Failed to load leads");
    } finally {
      setLoading(false);
    }
  }, [statusFilter, search]);

  useEffect(() => {
    if (user && user !== false) fetchLeads();
  }, [user, fetchLeads]);

  const onSearch = (e) => {
    e.preventDefault();
    fetchLeads();
  };

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/leads/${id}`, { status });
      toast.success(`Marked as ${status}`);
      fetchLeads();
    } catch { toast.error("Update failed"); }
  };

  const removeLead = async (id) => {
    if (!window.confirm("Delete this lead?")) return;
    try {
      await api.delete(`/leads/${id}`);
      toast.success("Deleted");
      fetchLeads();
    } catch { toast.error("Delete failed"); }
  };

  const quoteWA = (lead) => {
    const msg = `Namaste ${lead.name}, Maa Vindhyavasini Packers Movers & Transport se — aapki shifting query mili.\n\nDetails:\nFrom: ${lead.from_city}\nTo: ${lead.to_city}\nBHK: ${lead.bhk}\nDate: ${lead.move_date || "TBD"}\n\nQuote: ${lead.estimated_price || "Free site visit ke baad final quote denge."}\n\nDiwali special: 10% off + free bubble wrap.\nKya aaj call kar sakte hain?`;
    window.open(whatsappHref(lead.phone, msg), "_blank");
  };

  const cards = useMemo(() => ([
    { key: "total", label: "Total Leads", value: stats.total, color: "bg-[#1E3A8A]" },
    { key: "new", label: "New", value: stats.by_status.new || 0, color: "bg-blue-500" },
    { key: "contacted", label: "Contacted", value: stats.by_status.contacted || 0, color: "bg-yellow-500" },
    { key: "quoted", label: "Quoted", value: stats.by_status.quoted || 0, color: "bg-orange-500" },
    { key: "booked", label: "Booked", value: stats.by_status.booked || 0, color: "bg-green-500" },
  ]), [stats]);

  if (user === null) return <div className="min-h-screen grid place-items-center">Loading...</div>;
  if (user === false) return <Navigate to="/admin/login" replace />;

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b sticky top-0 z-30 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-[#1E3A8A] to-[#F97316] grid place-items-center text-white font-black text-sm font-display">MV</div>
            <div className="font-display font-extrabold text-[#0F172A]">Owner Dashboard</div>
          </Link>
          <div className="flex items-center gap-3">
            <span className="text-sm text-slate-600 hidden sm:inline">{user.email}</span>
            <Button onClick={logout} variant="outline" size="sm" data-testid="admin-logout-btn">
              <LogOut className="w-4 h-4 mr-1" /> Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <ReviewAlertsBanner />

        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6" data-testid="admin-stats-cards">
          {cards.map((c) => (
            <Card key={c.key} className="p-4">
              <div className={`w-2 h-2 rounded-full ${c.color} mb-2`} />
              <div className="text-xs text-slate-500 uppercase tracking-wider">{c.label}</div>
              <div className="font-display text-2xl font-extrabold text-[#0F172A]">{c.value}</div>
            </Card>
          ))}
        </div>

        <Card className="p-4 mb-4">
          <form onSubmit={onSearch} className="grid sm:grid-cols-[1fr_200px_auto] gap-3">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-3 text-slate-400" />
              <Input placeholder="Search name, phone, city..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" data-testid="admin-search-input" />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger data-testid="admin-status-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button type="submit" className="bg-[#1E3A8A]" data-testid="admin-search-btn">Search</Button>
          </form>
        </Card>

        <Card className="overflow-x-auto">
          <Table data-testid="admin-leads-table">
            <TableHeader>
              <TableRow>
                <TableHead>Lead</TableHead>
                <TableHead>Route</TableHead>
                <TableHead>BHK</TableHead>
                <TableHead>Source</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={6} className="text-center py-12 text-slate-500">Loading leads...</TableCell></TableRow>
              ) : leads.length === 0 ? (
                <TableRow><TableCell colSpan={6} className="text-center py-12 text-slate-500">No leads yet. Share your website — first lead is around the corner!</TableCell></TableRow>
              ) : leads.map((l) => (
                <TableRow key={l.id}>
                  <TableCell>
                    <div className="font-semibold text-[#0F172A]">{l.name}</div>
                    <div className="text-xs text-slate-500">{l.phone}</div>
                    {l.estimated_price && <div className="text-xs text-[#F97316] font-semibold mt-0.5">{l.estimated_price}</div>}
                  </TableCell>
                  <TableCell>
                    <div className="text-sm">{l.from_city} → {l.to_city}</div>
                    <div className="text-xs text-slate-400">{l.move_date || ""}</div>
                  </TableCell>
                  <TableCell><Badge variant="outline">{l.bhk}</Badge></TableCell>
                  <TableCell><Badge variant="secondary" className="text-xs">{l.source}</Badge></TableCell>
                  <TableCell>
                    <Select value={l.status} onValueChange={(v) => updateStatus(l.id, v)}>
                      <SelectTrigger className={`w-28 h-8 ${STATUS_STYLE[l.status] || ""}`} data-testid={`admin-lead-status-select-${l.id}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button size="icon" variant="outline" className="h-8 w-8 text-[#22C55E]" onClick={() => quoteWA(l)} title="WhatsApp Quote" data-testid={`admin-wa-btn-${l.id}`}>
                        <MessageCircle className="w-4 h-4" />
                      </Button>
                      <Button size="icon" variant="outline" className="h-8 w-8 text-[#1E3A8A]" asChild title="Call">
                        <a href={telHref(l.phone)}><Phone className="w-4 h-4" /></a>
                      </Button>
                      <Button size="icon" variant="outline" className="h-8 w-8 text-red-500" onClick={() => removeLead(l.id)} title="Delete" data-testid={`admin-delete-btn-${l.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      </main>
    </div>
  );
}
